/**
 * 🔥 The hearth, open — T128, D96, protocol 16.
 *
 * 🎯 **haugur, 2026-08-27:** *"heart menu should show 2 slots for wood for
 * burnig that can hold 99 each, then we need 4 slots for charcoal to be saved
 * to, i would also want to be able to move wood and chacoal from my onhand
 * inventory with the mouse, that adds the role of charcoal storage to the
 * heath."*
 *
 * ## 🔴 Its own screen, and haugur is the reason
 *
 * Shown a proposal to make it a third tab beside `Pack` and `Craft`: *"if we put
 * all the menus as tabs on the same menu, there is no need for players to walk
 * to hearth or smíðabekkur, these menus should only be available if you interact
 * with these items."* ✅ `Development/Minecraft/04` §4 agrees in the same words —
 * stations **site** progression, *"go home, or build a second one"* — and marks
 * that job **load-bearing**. ⚠️ **A store you can reach from anywhere has
 * stopped being a place.**
 *
 * ## 🔴 Nothing here decides what anything looks like
 *
 * The marks come from `hud-parts.ts` and the arrangement from `hud-scene.ts`,
 * the same two files the approved sheets are drawn from — samples `43` and
 * `44`. This file is the plumbing: where the element sits, when it is rebuilt,
 * and what a click means.
 *
 * ## ⚠️ Nothing is carried by the SERVER
 *
 * Exactly as `pack.ts`: an item picked up here never leaves its slot as far as
 * the server is concerned. The carrying is a drawing, and only the finished move
 * is sent. So closing mid-carry, or losing the connection, puts nothing at risk.
 */

import { INVENTORY_CARRIED, INVENTORY_ON_HAND, t, type Stack } from '@lofi/shared';
import { hudScale, makeScene, type VariantName } from './hud-scene.ts';
import { slotContentFor } from './hud-cluster.ts';
import type { SlotContent } from './hud-parts.ts';

/** ✅ The variation haugur chose — the same one everything else draws in. */
const VARIANT: VariantName = 'scrim';

/** One end of a move: which container, and which slot in it. */
export interface HearthEnd {
  readonly where: 'hearth' | 'player';
  readonly slot: number;
}

/**
 * Asked of the server when a carried stack is put down.
 *
 * ⚠️ **Both ends name their container.** Both start at slot 0, and a bare pair
 * of numbers would let the reader guess — which puts the right item in the
 * wrong box with nothing raised anywhere.
 */
export type HearthMoveRequest = (
  at: { x: number; y: number; z: number },
  from: HearthEnd,
  to: HearthEnd,
  count: number,
) => void;

/** What the player has picked up and is holding over the panel. */
interface Carried {
  readonly from: HearthEnd;
  readonly itemKey: string;
  readonly count: number;
}

export class HearthPanel {
  private readonly svg: SVGSVGElement;
  private readonly body: SVGGElement;
  private readonly cursor: SVGGElement;
  private readonly onMove: HearthMoveRequest;
  /**
   * The sentences under the fire's row — what the fire is doing, and whether
   * this is your home — each `null` for silence. Written by the caller, in the
   * player's language; this file never sees a word of it.
   */
  private readonly words: (
    lit: boolean,
    hoursLeft: number,
    home: boolean,
  ) => { fire: string | null; home: string | null };

  /** The drawing's own units — see {@link scale}. */
  private width = 0;
  private height = 0;
  private rawW = 0;
  private rawH = 0;
  /** 🔍 How much bigger than the design frame this screen draws — T170. */
  private scale = 1;

  private open = false;
  /** Which hearth is open. `null` means none, and nothing draws. */
  private at: { x: number; y: number; z: number } | null = null;
  /** Eight counts: **0–1 fuel, 2–5 output, 6–7 what is to be cooked.** */
  private slots: readonly number[] = [];
  private lit = false;
  private hoursLeft = 0;
  /** 🏠 Whether this is the hearth you would wake at — protocol 22. */
  private home = false;
  /** 🔥 D135 — this fire is the pit-house oven: wood only. */
  private oven = false;
  /**
   * 🔥 **Which thing is in each of the eight slots**, straight from the server.
   *
   * 🔴 **The panel used to ASK a callback that only knew the slot NUMBER**, so
   * it could only ever answer "birch" — and it did, while the fire happily
   * burned driftwood. 🎯 haugur: *"i placed 2 but there where not 2 on stock in
   * the hearth."* ⚠️ **A slot's identity is a fact about the world, not about
   * which slot it is**, so it has to come from the server.
   *
   * 🔴 **THE CALLBACK IS GONE ENTIRELY SINCE D121, AND IT HAD TO GO.** It
   * survived as a *fallback* for the slots whose answer was thought to be fixed
   * — *"charcoal is charcoal"* — **and the output shelf holds four cooked fish
   * now.** ⚠️ **A fallback that is wrong is worse than none**: it would have
   * drawn charcoal over somebody's dinner rather than drawing an empty box, and
   * only in the case the server had gone quiet. **One way in.**
   */
  private slotItems: readonly (string | null)[] = [];

  private stacks: readonly Stack[] = [];
  private selected = 0;
  private hover: HearthEnd | null = null;
  private carried: Carried | null = null;

  constructor(
    onMove: HearthMoveRequest,
    words: (
      lit: boolean,
      hoursLeft: number,
      home: boolean,
    ) => { fire: string | null; home: string | null },
  ) {
    this.onMove = onMove;
    this.words = words;

    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    /* ⚠️ The pack's own class, so it inherits the overlay's position and its
       `pointer-events: auto`. Two screens of the same kind in the same place. */
    this.svg.setAttribute('class', 'packpanel');
    this.svg.setAttribute('aria-hidden', 'true');
    this.svg.style.display = 'none';

    this.body = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.cursor = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.svg.append(this.body, this.cursor);
    document.body.appendChild(this.svg);

    /* 🔴 A ResizeObserver, never a window `resize` listener — an `<svg>`
       measured before layout still has its default 300 × 150, nothing throws,
       and the whole screen comes out at the wrong scale looking deliberate. */
    new ResizeObserver(() => {
      this.measure();
    }).observe(this.svg);
    this.measure();

    this.svg.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.svg.addEventListener('mousedown', (e) => this.onMouseDown(e));
    this.svg.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  get isOpen(): boolean {
    return this.open;
  }

  /** Which hearth is open, so the caller can tell whether a message is for it. */
  get openAt(): { x: number; y: number; z: number } | null {
    return this.at;
  }

  setOpen(open: boolean): void {
    if (open === this.open) return;
    this.open = open;
    /* ⚠️ Closing puts a carried stack back where it came from. Nothing was ever
       sent, so there is nothing to undo — but a screen that reopened with a
       stack still on the cursor would be describing a state the server has
       never heard of. */
    this.carried = null;
    this.hover = null;
    if (!open) this.at = null;
    this.svg.style.display = open ? 'block' : 'none';
    this.draw();
  }

  /** What the server says this hearth holds — protocol 16. */
  setContents(
    at: { x: number; y: number; z: number },
    slots: readonly number[],
    lit: boolean,
    hoursLeft: number,
    slotItems: readonly (string | null)[] = [],
    home = false,
    oven = false,
  ): void {
    this.at = at;
    this.oven = oven;
    this.slots = slots;
    this.slotItems = slotItems;
    this.lit = lit;
    this.hoursLeft = hoursLeft;
    this.home = home;
    this.draw();
  }

  /** What the server says is in the pack — protocol 9. */
  setInventory(stacks: readonly Stack[], selected: number): void {
    this.stacks = stacks;
    this.selected = selected;
    this.draw();
  }

  private measure(): void {
    const w = this.svg.clientWidth;
    const h = this.svg.clientHeight;
    if (w === this.rawW && h === this.rawH) return;
    if (w <= 0 || h <= 0) return;
    this.rawW = w;
    this.rawH = h;
    this.scale = hudScale(w, h);
    this.width = w / this.scale;
    this.height = h / this.scale;
    this.svg.setAttribute('viewBox', `0 0 ${String(this.width)} ${String(this.height)}`);
    this.draw();
  }

  private scene(): ReturnType<typeof makeScene> {
    /* 🔴 `TOPBAR: 0` — this element already sits below the status bar, so the
       bar covers none of it. The 34 is for a sample sheet, whose SVG is the
       whole window; applying it here twice leaves an undimmed band across the
       top that looks entirely deliberate. */
    return makeScene({ SW: this.width, SH: this.height, TOPBAR: 0 });
  }

  /**
   * Where the mouse is, in the drawing's own coordinates.
   *
   * 🔴 **Divided by the zoom** — T170. One unit of this drawing is
   * {@link scale} pixels on the glass, and without the division a click lands
   * short of where the eye says, by more the further from the corner it is.
   */
  private pointAt(e: MouseEvent): { x: number; y: number } {
    const box = this.svg.getBoundingClientRect();
    return { x: (e.clientX - box.left) / this.scale, y: (e.clientY - box.top) / this.scale };
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.open) return;
    const { x, y } = this.pointAt(e);
    if (this.carried) this.cursor.setAttribute('transform', `translate(${String(x)},${String(y)})`);
    const over = this.scene().hearthSlotAt(x, y);
    /* ⚠️ Redrawn only when the slot under the mouse CHANGES. At mouse-move
       rates a rebuild per event is the fault `hud-cluster.ts` warns about. */
    if (same(over, this.hover)) return;
    this.hover = over;
    this.draw();
  }

  /**
   * 🖱️ What a click means. **The same four rules as the pack**, so one screen
   * does not teach a habit the other punishes.
   *
   * | | |
   * |---|---|
   * | **Left-click a slot with something in it** | Pick the whole stack up |
   * | **Right-click** | Pick up **half**, rounded up |
   * | **Click another slot while carrying** | Put it there. **The only thing that sends a message** |
   * | **Click the same slot, or off the panel** | Put it back. Nothing was sent |
   *
   * ⚠️ **Whether the move is ALLOWED is not decided here.** A wood slot
   * refusing charcoal, a full slot, a stack you do not own — those are the
   * server's answers, and it replies with both halves either way. **Two copies
   * of one rule is how the screen and the world start disagreeing.**
   */
  private onMouseDown(e: MouseEvent): void {
    if (!this.open || this.at === null) return;
    if (e.button !== 0 && e.button !== 2) return;
    e.preventDefault();

    const { x, y } = this.pointAt(e);
    const target = this.scene().hearthSlotAt(x, y);

    if (this.carried === null) {
      if (target === null) return;
      const count = this.countIn(target);
      if (count <= 0) return;
      const itemKey = this.itemIn(target);
      if (itemKey === undefined) return;
      /* Half, rounded UP — right-clicking a single item picks it up rather than
         picking up nothing, which looks like a dead click. */
      this.carried = {
        from: target,
        itemKey,
        count: e.button === 2 ? Math.ceil(count / 2) : count,
      };
      this.cursor.setAttribute('transform', `translate(${String(x)},${String(y)})`);
      this.draw();
      return;
    }

    const { from, count } = this.carried;
    this.carried = null;

    /* Off the panel, or back where it came from: put it down, send nothing. */
    if (target === null || same(target, from)) {
      this.draw();
      return;
    }

    this.draw();
    this.onMove(this.at, from, target, count);
  }

  /** How many are in one end, taking the carried stack out of its source. */
  private countIn(end: HearthEnd): number {
    const held = this.carried !== null && same(this.carried.from, end) ? this.carried.count : 0;
    if (end.where === 'hearth') {
      const n = this.slots.find((_unused, i) => i === end.slot) ?? 0;
      return n - held;
    }
    return (this.stacks.find((s) => s.slot === end.slot)?.count ?? 0) - held;
  }

  /** What item one end holds. */
  private itemIn(end: HearthEnd): string | undefined {
    if (end.where === 'hearth') return this.hearthItem(end.slot);
    return this.stacks.find((s) => s.slot === end.slot)?.itemKey;
  }

  /**
   * What this hearth slot holds — **what the SERVER says, and nothing else.**
   *
   * 🔴 **There is no fallback any more, and its removal is D121.** It answered
   * from the slot NUMBER, which can only ever be right while a group of slots
   * holds one known thing; the output shelf now holds charcoal **and** four
   * cooked fish. ⚠️ **An empty box is the honest drawing for a slot nobody has
   * named. A confidently wrong one is not.**
   */
  private hearthItem(slot: number): string | undefined {
    return this.slotItems.at(slot) ?? undefined;
  }

  private draw(): void {
    if (!this.open || this.width <= 0 || this.at === null) return;
    const scene = this.scene();

    /* 🔥 The hearth's six, as pictures. A count of zero draws an empty slot. */
    const hearth: SlotContent[] = Array.from({ length: scene.HEARTH_SLOT_COUNT }, (_unused, i) => {
      const count = this.countIn({ where: 'hearth', slot: i });
      const itemKey = this.hearthItem(i);
      if (count <= 0 || itemKey === undefined) return {};
      return slotContentFor({ slot: i, itemKey, count });
    });

    /* 🎒 The player's own things, in the two groups this screen draws.
       ⚠️ **Filled with empties first**, so slot 7 is drawn in position 7 even
       when 0-6 hold nothing — an array with holes would shuffle everything
       left, which looks like the items moved. */
    const hand: SlotContent[] = Array.from({ length: INVENTORY_ON_HAND }, () => ({}));
    const pack: SlotContent[] = Array.from(
      { length: INVENTORY_CARRIED - INVENTORY_ON_HAND },
      () => ({}),
    );
    for (const stack of this.stacks) {
      /* ⚠️ 34-37 are what you are WEARING and this screen does not draw them.
         Letting them through would index past the end of the pack array. */
      if (stack.slot < 0 || stack.slot >= INVENTORY_CARRIED) continue;
      const count = this.countIn({ where: 'player', slot: stack.slot });
      if (count <= 0) continue;
      const content = slotContentFor({ ...stack, count });
      if (stack.slot < INVENTORY_ON_HAND) hand[stack.slot] = content;
      else pack[stack.slot - INVENTORY_ON_HAND] = content;
    }

    const said = this.words(this.lit, this.hoursLeft, this.home);
    replace(
      this.body,
      scene.hearthPanel(VARIANT, hearth, pack, hand, this.selected, this.hover, {
        /* 🔴 Standards §8b — every sentence a player reads lives in `en.json`.
           The drawing carries English defaults so a sample sheet can be made
           without the loader; the GAME always hands the words down. */
        hearth: t(this.oven ? 'oven.title' : 'hearth.title'),
        oven: this.oven,
        wood: t('hearth.wood'),
        charcoal: t('hearth.charcoal'),
        toCook: t('hearth.toCook'),
        title: t('pack.title'),
        onHand: t('pack.onHand'),
        close: t('pack.close'),
        fire: said.fire,
        home: said.home,
        lit: this.lit,
      }),
    );
    this.drawCursor();
  }

  /**
   * The stack riding the cursor.
   *
   * 🔴 **Its own group so it can move without a redraw** — following the mouse
   * by rebuilding the panel would re-parse an icon sixty times a second.
   */
  private drawCursor(): void {
    if (this.carried === null) {
      replace(this.cursor, '');
      return;
    }
    const scene = this.scene();
    const half = scene.SLOT / 2;
    const content = slotContentFor({
      slot: 0,
      itemKey: this.carried.itemKey,
      count: this.carried.count,
    });
    replace(
      this.cursor,
      `<g transform="translate(${String(-half)},${String(-half)})" pointer-events="none">` +
        scene.P.slot('carved', 0, 0, content) +
        `</g>`,
    );
  }
}

/**
 * Put a fragment of SVG markup inside an element.
 *
 * ⚠️ **Parsed, not assigned to `innerHTML`** — the same rule and the same
 * reasoning as `pack.ts` and `hud-cluster.ts`: everything drawn here is ours
 * today, and the moment an item name comes from somewhere else this is the line
 * that would have had to change.
 */
function replace(target: SVGGElement, markup: string): void {
  if (markup === '') {
    target.replaceChildren();
    return;
  }
  const parsed = new DOMParser().parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${markup}</svg>`,
    'image/svg+xml',
  );
  const root = parsed.documentElement;
  if (root.nodeName === 'parsererror' || root.getElementsByTagName('parsererror').length > 0) {
    console.error('Hearth: could not parse the drawing', root.textContent);
    return;
  }
  target.replaceChildren(...Array.from(root.childNodes).map((n) => document.importNode(n, true)));
}

/** Two ends are the same place only when both halves agree. */
function same(a: HearthEnd | null, b: HearthEnd | null): boolean {
  if (a === null || b === null) return a === b;
  return a.where === b.where && a.slot === b.slot;
}
